import { useEffect, useState } from "react";
import Flex from "../Flex/Flex";
import Button from "../forms/Button/Button";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { TeamMember, useUserContext } from "@/contexts/UserContext";
import { AssistantPermissions, ErrorCode, UserRole } from "@boxem/core-api-schema";
import { inviteMember, updateMember } from "@/api/teams";
import Checkbox from "../forms/Checkbox/Checkbox";
import TextInput from "../forms/TextInput/TextInput";
import Divider from "../Divider/Divider";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "../Modal/Modal";
import { NotificationType, useNotificationContext } from "@/contexts/NotificationContext";


export interface MemberInviteModalModeProps {
  member?: TeamMember;
  open: boolean;
  onClose: () => void;
  team: TeamMember[],
  setTeam: (memebers: TeamMember[]) => void;
}

export default function MemberInviteModal(props: MemberInviteModalModeProps) {
  const { member, open, onClose, team, setTeam } = props;

  const { user, ownerUUID } = useUserContext();
  const { showNotification } = useNotificationContext();

  const [loading, setLoading] = useState<boolean>(false);
  const [okButtonText, setOkButtonText] = useState(member ? "Update" : "Invite");
  const [name, setName] = useState<string>(props.member ? props.member.name : '');
  const [email, setEmail] = useState<string>(member ? member.email : '');
  const [dashboardPermissionChecked, setDashboardPermissionChecked] = useState<boolean>(member ? ((member.permissions & AssistantPermissions.DASHBOARD_PERMISSION) > 0) : false);
  const [inventoryPermissionChecked, setInventoryPermissionChecked] = useState<boolean>(member ? ((member.permissions & AssistantPermissions.INVENTORY_PERMISSION) > 0) : false);
  const [createShipmentPermissionChecked, setCreateShipmentPermissionChecked] = useState<boolean>(member ? ((member.permissions & AssistantPermissions.CREATE_SHIPMENTS_PERMISSION) > 0) : false);
  const [manageShipmentPermissionChecked, setManageShipmentPermissionChecked] = useState<boolean>(member ? ((member.permissions & AssistantPermissions.MANAGE_SHIPMENT_PERMISSION) > 0) : false);
  const [topSectionVisible, setTopSectionVisible] = useState<boolean>(true);

  const titleText = member ? "Update Permissions" : "Invite User";

  const handleSave = () => {
    setLoading(true);
    if (!!email && !!name) {
      const permissions = (dashboardPermissionChecked ? AssistantPermissions.DASHBOARD_PERMISSION : 0)
        | (inventoryPermissionChecked ? AssistantPermissions.INVENTORY_PERMISSION : 0)
        | (createShipmentPermissionChecked ? AssistantPermissions.CREATE_SHIPMENTS_PERMISSION : 0)
        | (manageShipmentPermissionChecked ? AssistantPermissions.MANAGE_SHIPMENT_PERMISSION : 0);

      if (member) {
        // update member
        member.permissions = permissions;
        updateMember(member).then(() => {
          showNotification("Member permissions updated successfully!", NotificationType.SUCCESS);
          onClose();
        }).catch((err) => {
          console.log(err);
          showNotification("Member permission update failed. Please try again later.", NotificationType.ERROR);
        }).finally(() => {
          setLoading(false);
          setName("");
          setEmail("");
        });
      } else {
        // invite new member
        const newMember: TeamMember = {
          name: name,
          email: email,
          permissions: permissions,
        }
        inviteMember(ownerUUID ?? user.uuid, newMember).then(() => {
          const teamMembers = team ? [...team, newMember] : [newMember];
          setTeam(teamMembers);
          showNotification("Member invited successfully!", NotificationType.SUCCESS);
          onClose();
        }).catch((err) => {
          //console.log(err);
          const msg = err[0]?.type === ErrorCode.SERVER_QUOTA_EXCEEDED ? "You have reached the maximum number of users you can invite." : "Member invite failed. Please try again later.";
          showNotification(msg, NotificationType.ERROR);
        }).finally(() => {
          setLoading(false);
          setName("");
          setEmail("");
        })
      }
    } else {
      showNotification("Please fill in name and email", NotificationType.ERROR);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (member) {
      setName(member.name);
      setEmail(member.email);
      setDashboardPermissionChecked(member ? ((member.permissions & AssistantPermissions.DASHBOARD_PERMISSION) > 0) : false);
      setInventoryPermissionChecked(member ? ((member.permissions & AssistantPermissions.INVENTORY_PERMISSION) > 0) : false);
      setCreateShipmentPermissionChecked(member ? ((member.permissions & AssistantPermissions.CREATE_SHIPMENTS_PERMISSION) > 0) : false);
      setManageShipmentPermissionChecked(member ? ((member.permissions & AssistantPermissions.MANAGE_SHIPMENT_PERMISSION) > 0) : false);
      setOkButtonText('Update');
      setTopSectionVisible(false);
    }
    else {
      setTopSectionVisible(true);
      setOkButtonText('Invite');
      setName('');
      setEmail('');
      setDashboardPermissionChecked(false);
      setInventoryPermissionChecked(false);
      setCreateShipmentPermissionChecked(false);
      setManageShipmentPermissionChecked(false);
    }
  }, [member])

  return (
    <Modal
      open={open}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <ModalHeader>
        <h3>{titleText}</h3>
      </ModalHeader>
      
      <ModalBody>
        <Flex direction="column" justify="space-between" align="center">
          {topSectionVisible &&
            <Flex direction="column" gap={2} style={{ margin: "20px" }}>
              <label>{"Name"}</label>
              <TextInput value={name} onChange={e => setName(e)} />
            </Flex>
          }

          {topSectionVisible &&
            <Flex direction="column" gap={2} style={{ margin: "20px" }}>
              <label>{"Email"}</label>
              <TextInput type="email" maxLength={100} value={email} onChange={e => setEmail(e)} />
            </Flex>
          }
          <Divider />
          <Flex direction="column" style={{ width: '100%', padding: '20px' }}>
            <label>Permissions:</label>
            <Flex direction="row" gap={8} style={{ marginTop: "10px" }}>
              <Flex direction="column" gap={4} justify="space-between" style={{ margin: '10px' }}>
                <Flex gap={2}>
                  <label>
                    <Checkbox
                      value={dashboardPermissionChecked}
                      onChange={(e) => {
                        setDashboardPermissionChecked(e);
                      }}
                    />
                    Dashboard
                  </label>
                </Flex>

                <Flex gap={2}>
                  <label>
                    <Checkbox
                      value={inventoryPermissionChecked}
                      onChange={(e) => {
                        setInventoryPermissionChecked(e);
                      }}
                    />
                    Inventory
                  </label>
                </Flex>
              </Flex>

              <Flex direction="column" justify="space-between" style={{ margin: "10px" }}>
                <Flex gap={2}>
                  <label>
                    <Checkbox
                      value={createShipmentPermissionChecked}
                      onChange={(e) => {
                        setCreateShipmentPermissionChecked(e)
                      }}
                    />
                    Create Shipment
                  </label>
                </Flex>

                <Flex gap={2}>
                  <label>
                    <Checkbox
                      value={manageShipmentPermissionChecked}
                      onChange={(e) => {
                        setManageShipmentPermissionChecked(e);
                      }}
                    />
                    Manage Shipment
                  </label>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </ModalBody>
      
      <ModalFooter>
        <Flex
          justify="end"
          gap={2}
        >
          {loading
            ? <LoadingSpinner />
            : <>
              <Button
                type="secondary"
                onClick={() => onClose()}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleSave}
              >
                {okButtonText}
              </Button>
            </>
          }
        </Flex>
      </ModalFooter>
    </Modal>
  );
}
